import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Save, Building2, Receipt, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const INDUSTRIES = ["Marketing & Advertising", "SaaS / Tech", "E-commerce", "Healthcare", "Finance", "Education", "Manufacturing", "Real estate", "Consumer / Retail", "Non-profit", "Government", "Other"];
const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const TAX_LABELS = ["GSTIN", "PAN", "VAT", "TIN", "EIN", "ABN", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];
const PAYMENT_TERMS = [0, 7, 15, 30, 45, 60, 90];

export default function ClientOverviewTab({ client, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      client_code: client?.client_code || "",
      display_name: client?.display_name || "",
      legal_name: client?.legal_name || "",
      industry: client?.industry || "",
      company_size: client?.company_size || "",
      website: client?.website || "",
      contact_email: client?.contact_email || "",
      contact_phone: client?.contact_phone || "",
      billing_address_line1: client?.billing_address_line1 || "",
      billing_address_line2: client?.billing_address_line2 || "",
      billing_city: client?.billing_city || "",
      billing_state: client?.billing_state || "",
      billing_country: client?.billing_country || "",
      billing_postal_code: client?.billing_postal_code || "",
      tax_id_label: client?.tax_id_label || "",
      tax_id_value: client?.tax_id_value || "",
      billing_email: client?.billing_email || "",
      billing_currency: client?.billing_currency || "",
      payment_terms_days: client?.payment_terms_days ?? "",
      purchase_order_required: client?.purchase_order_required ?? false,
      credit_rating: client?.credit_rating || "",
      csat_score: client?.csat_score ?? "",
      retention_risk: client?.retention_risk || "",
      retention_risk_notes: client?.retention_risk_notes || "",
      renewal_date: client?.renewal_date || "",
      notes: client?.notes || "",
    });
  }, [client?.id]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.payment_terms_days === "") payload.payment_terms_days = null;
      else if (payload.payment_terms_days != null) payload.payment_terms_days = Number(payload.payment_terms_days);
      if (payload.csat_score === "") payload.csat_score = null;
      if (!payload.renewal_date) payload.renewal_date = null;
      if (!payload.credit_rating) payload.credit_rating = null;
      if (!payload.retention_risk) payload.retention_risk = null;
      // Pydantic EmailStr rejects "" — omit blank email fields entirely
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.billing_email) delete payload.billing_email;
      await api.patch(`/clients/${client.id}`, payload);
      toast.success("Client details saved");
      onSaved && (await onSaved());
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="client-overview-tab">
      <Section icon={Building2} title="Company">
        <Two>
          <Field label="Client code" value={form.client_code} onChange={(v) => set("client_code", v)} testid="overview-client-code" placeholder="ACM-001" />
          <Field label="Display name" value={form.display_name} onChange={(v) => set("display_name", v)} testid="overview-display-name" placeholder="Acme (short label)" />
        </Two>
        <Two>
          <Field label="Legal name" value={form.legal_name} onChange={(v) => set("legal_name", v)} testid="overview-legal-name" placeholder="ACME Industries Pvt. Ltd." />
          <SelectField label="Industry" value={form.industry} onChange={(v) => set("industry", v)} options={INDUSTRIES} testid="overview-industry" />
        </Two>
        <Two>
          <SelectField label="Company size" value={form.company_size} onChange={(v) => set("company_size", v)} options={SIZES} testid="overview-size" />
          <Field label="Website" value={form.website} onChange={(v) => set("website", v)} placeholder="https://acmecorp.com" testid="overview-website" icon={Globe} />
        </Two>
        <Two>
          <Field label="Primary phone" value={form.contact_phone} onChange={(v) => set("contact_phone", v)} placeholder="+91 98765 43210" testid="overview-phone" />
          <Field label="Primary email" type="email" value={form.contact_email} onChange={(v) => set("contact_email", v)} placeholder="hello@acmecorp.com" testid="overview-email" />
        </Two>
      </Section>

      <Section icon={MapPin} title="Billing address">
        <Field label="Address line 1" value={form.billing_address_line1} onChange={(v) => set("billing_address_line1", v)} testid="overview-addr1" />
        <Field label="Address line 2" value={form.billing_address_line2} onChange={(v) => set("billing_address_line2", v)} testid="overview-addr2" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="City" value={form.billing_city} onChange={(v) => set("billing_city", v)} testid="overview-city" />
          <Field label="State / Province" value={form.billing_state} onChange={(v) => set("billing_state", v)} testid="overview-state" />
          <Field label="Postal code" value={form.billing_postal_code} onChange={(v) => set("billing_postal_code", v)} testid="overview-postal" />
        </div>
        <Field label="Country" value={form.billing_country} onChange={(v) => set("billing_country", v)} testid="overview-country" />
      </Section>

      <Section icon={Receipt} title="Tax & invoicing">
        <Two>
          <SelectField label="Tax ID label" value={form.tax_id_label} onChange={(v) => set("tax_id_label", v)} options={TAX_LABELS} testid="overview-tax-label" />
          <Field label="Tax ID value" value={form.tax_id_value} onChange={(v) => set("tax_id_value", v)} placeholder="29ABCDE1234F1Z5" testid="overview-tax-value" />
        </Two>
        <Field label="Billing email" type="email" value={form.billing_email} onChange={(v) => set("billing_email", v)} placeholder="ap@acmecorp.com" testid="overview-billing-email" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SelectField label="Billing currency" value={form.billing_currency} onChange={(v) => set("billing_currency", v)} options={CURRENCIES} testid="overview-currency" />
          <SelectField label="Payment terms (days)" value={String(form.payment_terms_days || "")} onChange={(v) => set("payment_terms_days", v ? Number(v) : "")} options={PAYMENT_TERMS.map(String)} renderItem={(o) => o === "0" ? "Due on receipt" : `Net ${o}`} testid="overview-payment-terms" />
          <label className="flex items-end gap-3 text-sm">
            <input
              type="checkbox"
              checked={!!form.purchase_order_required}
              onChange={(e) => set("purchase_order_required", e.target.checked)}
              className="h-4 w-4 mb-3"
              data-testid="overview-po-required"
            />
            <span className="pb-2.5">PO required on invoices</span>
          </label>
        </div>
      </Section>

      <Section title="Client health & relationship">
        <Two>
          <SelectField label="Credit rating" value={form.credit_rating} onChange={(v) => set("credit_rating", v)}
                          options={["AAA", "AA", "A", "BBB", "BB", "B", "not_rated"]}
                          renderItem={(o) => o === "not_rated" ? "Not rated" : o}
                          testid="overview-credit-rating" />
          <SelectField label="Retention risk" value={form.retention_risk} onChange={(v) => set("retention_risk", v)}
                          options={["low", "medium", "high", "churning"]}
                          testid="overview-retention-risk" />
        </Two>
        <Two>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">CSAT / Satisfaction (1–5)</Label>
            <Input type="number" min={1} max={5} step={0.1}
                   value={form.csat_score === "" ? "" : form.csat_score}
                   onChange={(e) => set("csat_score", e.target.value === "" ? "" : Number(e.target.value))}
                   className="h-10" data-testid="overview-csat" />
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Renewal date</Label>
            <Input type="date" value={form.renewal_date || ""} onChange={(e) => set("renewal_date", e.target.value)} className="h-10" data-testid="overview-renewal-date" />
          </div>
        </Two>
        <div>
          <Label className="block mb-1.5 text-sm font-semibold">Retention risk drivers / notes</Label>
          <Textarea rows={3} value={form.retention_risk_notes} onChange={(e) => set("retention_risk_notes", e.target.value)}
                    placeholder="Slow response times, competitor engaged, unhappy with recent deliverables…"
                    data-testid="overview-retention-notes" />
        </div>
      </Section>

      <Section title="Internal notes">
        <Textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything teammates should know about this account…" data-testid="overview-notes" />
      </Section>

      <div className="sticky bottom-2 z-10 flex justify-end pt-4">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="overview-save">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save overview"}
        </Button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="border border-border p-5 space-y-3">
      <div className="label-eyebrow inline-flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Two({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, renderItem, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">—</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{renderItem ? renderItem(o) : o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
